import { log } from './vueLogger.js';

// Registry of every live CacheManager so a single memory-pressure flush can drop all
// in-heap caches at once (see clearAllMemoryCaches, wired into the memory-pressure seam).
const cacheManagerInstances = new Set();

export class CacheManager {
	constructor(dbName, storeName, options = {}) {
		this.dbName = dbName;
		this.dbVersion = 1;
		this.storeName = storeName;
		this.keyPath = options.keyPath || 'key';
		this.db = null;
		this.maxAge = options.maxAge || 7 * 24 * 60 * 60 * 1000;
		this.memoryCache = new Map();
		this.memoryCacheTTL = options.memoryCacheTTL || 5 * 60 * 1000;
		this.memoryCacheMaxEntries = options.memoryCacheMaxEntries || 200;
		this.logCategory = options.logCategory || 'Cache';
		this.keyPrefix = options.keyPrefix || null;
		cacheManagerInstances.add(this);
		this.initDB();
	}

	resolveKey(key) {
		if (!this.keyPrefix) return key;
		const prefix = typeof this.keyPrefix === 'function' ? this.keyPrefix() : this.keyPrefix;
		return `${prefix}${key}`;
	}

	async initDB() {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, this.dbVersion);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				this.db = request.result;
				resolve(this.db);
			};

			request.onupgradeneeded = (event) => {
				const db = event.target.result;
				if (!db.objectStoreNames.contains(this.storeName)) {
					const store = db.createObjectStore(this.storeName, { keyPath: this.keyPath });
					store.createIndex('timestamp', 'timestamp', { unique: false });
				}
			};
		});
	}

	async put(key, data) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const cacheData = {
				[this.keyPath]: this.resolveKey(key),
				data: data,
				timestamp: Date.now(),
			};

			const request = store.put(cacheData);
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async get(key) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);

			const request = store.get(this.resolveKey(key));
			request.onsuccess = () => {
				const result = request.result;
				resolve(result ? result.data : null);
			};
			request.onerror = () => reject(request.error);
		});
	}

	/**
	 * Batch-reads multiple keys in a single IndexedDB transaction.
	 * @param {string[]} keys
	 * @returns {Promise<Map<string, any|null>>} keyed by the original (unprefixed) key
	 */
	async getMany(keys) {
		if (!this.db) await this.initDB();
		if (keys.length === 0) return new Map();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);
			const results = new Map();
			let pending = keys.length;

			keys.forEach((key) => {
				const request = store.get(this.resolveKey(key));
				request.onsuccess = () => {
					results.set(key, request.result ? request.result.data : null);
					pending--;
					if (pending === 0) resolve(results);
				};
				request.onerror = () => reject(request.error);
			});
		});
	}

	async delete(key) {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const request = store.delete(this.resolveKey(key));
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async clear() {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);

			const request = store.clear();
			request.onsuccess = () => resolve();
			request.onerror = () => reject(request.error);
		});
	}

	async cleanOldEntries(maxAge = null) {
		if (!this.db) await this.initDB();

		const cutoff = Date.now() - (maxAge || this.maxAge);

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readwrite');
			const store = transaction.objectStore(this.storeName);
			const index = store.index('timestamp');

			const request = index.openCursor(IDBKeyRange.upperBound(cutoff));
			request.onsuccess = (event) => {
				const cursor = event.target.result;
				if (cursor) {
					cursor.delete();
					cursor.continue();
				} else {
					resolve();
				}
			};
			request.onerror = () => reject(request.error);
		});
	}

	getFromMemory(key) {
		const resolvedKey = this.resolveKey(key);
		const memCached = this.memoryCache.get(resolvedKey);
		if (!memCached) return null;

		// Expired: drop it so stale entries can't accumulate (getFromMemory previously
		// returned null on expiry but never deleted, so the Map only ever grew).
		if (Date.now() - memCached.timestamp >= this.memoryCacheTTL) {
			this.memoryCache.delete(resolvedKey);
			return null;
		}

		// Fresh hit — refresh recency for LRU eviction.
		this.memoryCache.delete(resolvedKey);
		this.memoryCache.set(resolvedKey, memCached);
		return memCached.data;
	}

	setInMemory(key, data) {
		const resolvedKey = this.resolveKey(key);
		this.memoryCache.delete(resolvedKey);
		this.memoryCache.set(resolvedKey, {
			data: data,
			timestamp: Date.now(),
		});
		// Bound the cache: evict least-recently-used entries past the cap.
		while (this.memoryCache.size > this.memoryCacheMaxEntries) {
			const oldestKey = this.memoryCache.keys().next().value;
			this.memoryCache.delete(oldestKey);
		}
	}

	deleteFromMemory(key) {
		this.memoryCache.delete(this.resolveKey(key));
	}

	clearMemory() {
		this.memoryCache.clear();
	}

	async getStats() {
		if (!this.db) await this.initDB();

		return new Promise((resolve, reject) => {
			const transaction = this.db.transaction([this.storeName], 'readonly');
			const store = transaction.objectStore(this.storeName);

			const countRequest = store.count();
			countRequest.onsuccess = () => {
				resolve({
					persistentEntries: countRequest.result,
					memoryEntries: this.memoryCache.size,
				});
			};
			countRequest.onerror = () => reject(countRequest.error);
		});
	}

	async clearAll() {
		log('info', this.logCategory, `🗱️ Clearing ${this.dbName} cache...`);

		try {
			await this.clear();
			this.clearMemory();
			log('success', this.logCategory, `✅ ${this.dbName} cache cleared successfully`);
		} catch (error) {
			log('error', this.logCategory, `❌ Failed to clear ${this.dbName} cache: ${error.message}`);
		}
	}

	async cleanOld(maxAgeInDays = null) {
		const maxAge = maxAgeInDays ? maxAgeInDays * 24 * 60 * 60 * 1000 : this.maxAge;
		try {
			await this.cleanOldEntries(maxAge);
		} catch (err) {
			log('error', this.logCategory, `Clean cache error: ${err.message}`);
		}
	}
}

/**
 * Drop the in-heap (memoryCache) layer of every live CacheManager, keeping IndexedDB
 * intact. Cheap way to shed RAM under memory pressure without losing offline data.
 */
export function clearAllMemoryCaches() {
	let cleared = 0;
	for (const cm of cacheManagerInstances) {
		cleared += cm.memoryCache.size;
		cm.clearMemory();
	}
	log('info', 'Cache', `🧹 Cleared in-memory layer of ${cacheManagerInstances.size} caches (${cleared} entries)`);
}
